/** Character-card containers: PNG metadata and CCv3 CHARX archives. */
import { Inflate, Unzlib } from 'fflate';
import { readFileText } from './fileText';
import { PresetImportError } from './presetTransfer';

export const CHARACTER_IMPORT_ACCEPT = '.json,.png,.charx,application/json,image/png';
export const WORLD_BOOK_IMPORT_ACCEPT = '.json,application/json';
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_JSON_BYTES = 5 * 1024 * 1024;
const MAX_AVATAR_BYTES = 10 * 1024 * 1024;
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

export interface PresetImportFile {
  text: string;
  avatar?: string;
}

function fail(message: string): never {
  throw new PresetImportError(`导入失败：${message}`);
}

function utf8(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return fail('角色卡文本不是有效的 UTF-8');
  }
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return btoa(binary);
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function inflateLimited(bytes: Uint8Array, raw: boolean, limit: number): Uint8Array {
  const parts: Uint8Array[] = [];
  let size = 0;
  const onData = (part: Uint8Array) => {
    size += part.length;
    if (size > limit) fail('解压后的内容过大');
    parts.push(part);
  };
  try {
    const stream = raw ? new Inflate(onData) : new Unzlib(onData);
    for (let offset = 0; offset < bytes.length; offset += 4096) {
      stream.push(bytes.subarray(offset, offset + 4096), offset + 4096 >= bytes.length);
    }
  } catch (error) {
    if (error instanceof PresetImportError) throw error;
    fail('压缩数据已损坏');
  }
  const result = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

async function readBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => reader.result instanceof ArrayBuffer
      ? resolve(new Uint8Array(reader.result))
      : reject(new PresetImportError('读取文件失败'));
    reader.onerror = () => reject(reader.error ?? new PresetImportError('读取文件失败'));
    reader.readAsArrayBuffer(file);
  });
}

function pngText(type: string, data: Uint8Array): { keyword: string; value: Uint8Array } | undefined {
  const separator = data.indexOf(0);
  if (separator < 1 || separator > 79) return undefined;
  const keyword = String.fromCharCode(...data.subarray(0, separator));
  if (keyword !== 'ccv3' && keyword !== 'chara') return undefined;
  let value = data.subarray(separator + 1);
  if (type === 'zTXt') {
    if (value[0] !== 0) fail('PNG 文本压缩格式不受支持');
    value = inflateLimited(value.subarray(1), false, MAX_JSON_BYTES * 2);
  } else if (type === 'iTXt') {
    const compressed = value[0];
    if ((compressed !== 0 && compressed !== 1) || value[1] !== 0) fail('PNG 文本格式无效');
    const languageEnd = value.indexOf(0, 2);
    const translatedEnd = languageEnd < 0 ? -1 : value.indexOf(0, languageEnd + 1);
    if (translatedEnd < 0) fail('PNG 文本格式无效');
    value = value.subarray(translatedEnd + 1);
    if (compressed === 1) value = inflateLimited(value, false, MAX_JSON_BYTES * 2);
  }
  return { keyword, value };
}

function readPng(bytes: Uint8Array): PresetImportFile {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const metadata = new Map<string, Uint8Array>();
  let offset = PNG_SIGNATURE.length;
  let ended = false;
  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset);
    const end = offset + 12 + length;
    if (end > bytes.length) fail('PNG 文件不完整');
    const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
    if (['tEXt', 'zTXt', 'iTXt'].includes(type)) {
      if (length > MAX_JSON_BYTES * 2) fail('PNG 角色卡数据过大');
      if (crc32(bytes.subarray(offset + 4, end - 4)) !== view.getUint32(end - 4)) {
        fail('PNG 角色卡数据校验失败');
      }
      const item = pngText(type, bytes.subarray(offset + 8, end - 4));
      if (item) metadata.set(item.keyword, item.value);
    }
    offset = end;
    if (type === 'IEND') { ended = true; break; }
  }
  if (!ended) fail('PNG 文件不完整');
  const encoded = metadata.get('ccv3') ?? metadata.get('chara');
  if (!encoded) fail('PNG 中没有角色卡数据，请选择包含角色卡信息的 PNG');
  let decoded: Uint8Array;
  try {
    decoded = Uint8Array.from(atob(utf8(encoded).trim()), (char) => char.charCodeAt(0));
  } catch {
    return fail('PNG 角色卡的 Base64 数据无效');
  }
  if (decoded.length > MAX_JSON_BYTES) fail('角色卡 JSON 过大');
  return { text: utf8(decoded), ...(bytes.length <= MAX_AVATAR_BYTES ? { avatar: `data:image/png;base64,${base64(bytes)}` } : {}) };
}

interface ZipEntry {
  name: string;
  flags: number;
  method: number;
  crc: number;
  size: number;
  compressedSize: number;
  localOffset: number;
}

function readCharx(bytes: Uint8Array): PresetImportFile {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = bytes.length - 22;
  for (; end >= Math.max(0, bytes.length - 65557); end--) {
    if (view.getUint32(end, true) === 0x06054b50 && end + 22 + view.getUint16(end + 20, true) === bytes.length) break;
  }
  if (end < Math.max(0, bytes.length - 65557)) fail('CHARX 压缩包目录无效');
  const count = view.getUint16(end + 10, true);
  if (view.getUint16(end + 4, true) !== 0 || view.getUint16(end + 6, true) !== 0 || view.getUint16(end + 8, true) !== count || count > 2048) {
    fail('不支持分卷或过大的 CHARX 压缩包');
  }
  const directorySize = view.getUint32(end + 12, true);
  const directoryOffset = end - directorySize;
  const archiveBase = directoryOffset - view.getUint32(end + 16, true);
  if (directoryOffset < 0 || archiveBase < 0) fail('CHARX 压缩包目录无效');
  const entries = new Map<string, ZipEntry>();
  let cursor = directoryOffset;
  for (let index = 0; index < count; index++) {
    if (cursor + 46 > end || view.getUint32(cursor, true) !== 0x02014b50) fail('CHARX 压缩包目录损坏');
    const nameLength = view.getUint16(cursor + 28, true);
    const next = cursor + 46 + nameLength + view.getUint16(cursor + 30, true) + view.getUint16(cursor + 32, true);
    if (next > end) fail('CHARX 压缩包目录损坏');
    const name = utf8(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    if (name.startsWith('/') || name.includes('\\') || /^[a-z]:/iu.test(name) || name.split('/').some((part) => part === '..' || part === '.')) {
      fail('CHARX 包含不安全的文件路径');
    }
    if (entries.has(name)) fail('CHARX 压缩包存在同名文件');
    entries.set(name, {
      name,
      flags: view.getUint16(cursor + 8, true),
      method: view.getUint16(cursor + 10, true),
      crc: view.getUint32(cursor + 16, true),
      compressedSize: view.getUint32(cursor + 20, true),
      size: view.getUint32(cursor + 24, true),
      localOffset: archiveBase + view.getUint32(cursor + 42, true),
    });
    cursor = next;
  }
  if (cursor !== end) fail('CHARX 压缩包目录无效');
  function extract(entry: ZipEntry, limit: number): Uint8Array {
    if ((entry.flags & 1) !== 0) fail('不支持加密 CHARX');
    if (entry.size > limit) fail('CHARX 中的角色卡或头像过大');
    const start = entry.localOffset;
    if (start < 0 || start + 30 > directoryOffset || view.getUint32(start, true) !== 0x04034b50) fail('CHARX 文件数据损坏');
    const nameEnd = start + 30 + view.getUint16(start + 26, true);
    const dataStart = nameEnd + view.getUint16(start + 28, true);
    const dataEnd = dataStart + entry.compressedSize;
    if (dataEnd > directoryOffset || dataStart > dataEnd || utf8(bytes.subarray(start + 30, nameEnd)) !== entry.name) fail('CHARX 文件数据损坏');
    const compressed = bytes.subarray(dataStart, dataEnd);
    const output = entry.method === 0 ? compressed : entry.method === 8
      ? inflateLimited(compressed, true, limit) : fail('CHARX 使用了不支持的压缩方式');
    if (output.length !== entry.size || output.length > limit || crc32(output) !== entry.crc) fail('CHARX 文件校验失败');
    return output;
  }
  const cardEntry = entries.get('card.json');
  if (!cardEntry) fail('CHARX 中缺少 card.json');
  const text = utf8(extract(cardEntry, MAX_JSON_BYTES));
  let card: { data?: { assets?: { type?: unknown; name?: unknown; uri?: unknown }[] } };
  try { card = JSON.parse(text) as typeof card; } catch { return fail('CHARX 中的 card.json 不是合法 JSON'); }
  const assets = Array.isArray(card?.data?.assets) ? card.data.assets : [];
  const icon = assets.find((asset) => asset.type === 'icon' && asset.name === 'main' && typeof asset.uri === 'string');
  const uri = icon?.uri;
  if (typeof uri !== 'string' || !uri.startsWith('embeded://')) return { text };
  const path = uri.slice('embeded://'.length);
  if (path.startsWith('/') || path.includes('\\') || path.split('/').some((part) => part === '..' || part === '.')) return { text };
  const avatarEntry = entries.get(path);
  if (!avatarEntry) return { text };
  const mime = /\.png$/iu.test(path) ? 'image/png' : /\.jpe?g$/iu.test(path) ? 'image/jpeg' : /\.webp$/iu.test(path) ? 'image/webp' : undefined;
  if (!mime) return { text };
  return { text, avatar: `data:${mime};base64,${base64(extract(avatarEntry, MAX_AVATAR_BYTES))}` };
}

/** Reads selected user files locally; archive assets are never written to disk or fetched. */
export async function readPresetImportFile(file: File): Promise<PresetImportFile> {
  if (file.size > MAX_FILE_BYTES) fail('文件不能超过 50 MB');
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  if (extension === 'png' || extension === 'charx' || file.type === 'image/png') {
    const bytes = await readBytes(file);
    if (PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)) return readPng(bytes);
    if (extension === 'charx') return readCharx(bytes);
    fail('文件不是有效的 PNG 角色卡');
  }
  if (file.size > MAX_JSON_BYTES) fail('JSON 文件不能超过 5 MB');
  return { text: await readFileText(file) };
}
